import { useEffect, useState } from 'react';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { useConfig } from '../context/ConfigContext';
import {
  startAdminSession, getAdminSession, clearAdminActivity, touchAdminActivity, initializeDefaultUser,
} from '../utils/authServiceUtil';
import { isPosAdmin } from '../utils/posClientUtil';
import { useFirebaseAccount } from '../hooks/useFirebaseAccount';
import { POS_ADMIN_TABS, POS_ADMIN_EMAILS, POS_ADMIN_AUDIT_ACTIONS } from '../constants/posConst';
import { BRAND_LOGO_URL, TAB_DISPLAY_NAME } from '../constants/bookingConst';
import {
  LOGIN_ADMIN_SUBTITLE, LOGIN_FORGOT_ADMIN, LOGIN_SIGN_OUT_AUDIT_WAIT_MS,
} from '../constants/posLoginConst';
import { logPosAdminEvent } from '../utils/posSubscriptionUtil';
import { loadingSpinner } from '../utils/bookingUtils';
import PosLoginScreen from './PosLoginScreen';
import PosLoginForm from './PosLoginForm';
import PosTopBar from './PosTopBar';
import PosSidebar from './PosSidebar';
import PosClientList from './PosClientList';
import PosClientForm from './PosClientForm';
import PosAdminConfigScreen from './PosAdminConfigScreen';
import PosSubscriptionPlans from './PosSubscriptionPlans';
import PosSignupRequests from './PosSignupRequests';
import '../css/Pos.css';
import '../css/PosAdmin.css';

/**
 * /admin — the estate: who the clients are, and what applies to all of them.
 *
 * ADMIN ONLY, in two places. This screen refuses any account that is not in
 * POS_ADMIN_EMAILS, and database.rules.json refuses the same addresses' writes
 * against auth.token.email. The check here is what stops the screen rendering;
 * the check there is what actually protects the data, since anyone can call
 * the database without going through this page.
 *
 * A signed-in non-admin is signed straight back out. Leaving the session open
 * would let the rest of the app act as an account that got no further than a
 * refused screen, which is a confusing state to debug and a pointless one to
 * hold.
 *
 * THIS SCREEN HOLDS THE BROWSER'S FIREBASE SESSION, and it is the only one
 * that does. The rules identify an administrator by auth.token.email, so this
 * is the one place that genuinely needs to BE the account rather than merely
 * know it. The tills verify their staff on a throwaway auth instance instead
 * (see verifyCredentials), which is what lets this screen and any number of
 * client tills be signed in as different people in the same browser.
 *
 * It still watches the account, because a sign-out elsewhere — or a second
 * administrator signing in — would leave every read below refused.
 *
 * @returns {JSX.Element}
 */
function PosAdminPage() {
  const { config, loading: configLoading } = useConfig();
  const idleTimeoutEnabled = config?.adminIdleTimeoutEnabled !== false;

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [signedInEmail, setSignedInEmail] = useState('');
  const [loginError, setLoginError] = useState('');

  const account = useFirebaseAccount();
  const [tab, setTab] = useState(POS_ADMIN_TABS[0].key);
  // The menu drawer, opened from the top bar.
  const [menuOpen, setMenuOpen] = useState(false);
  // The client being edited, or null when registering a new one. Held here
  // rather than in the registration screen so picking Edit on a card can open
  // that screen with the client already in it.
  const [editing, setEditing] = useState(null);

  // Restores a session still inside its idle window — but only an admin one.
  // The same localStorage record backs the till, so without the membership
  // check a signed-in member of staff would walk straight into this screen.
  useEffect(() => {
    const session = getAdminSession(idleTimeoutEnabled);
    if (session?.email && isPosAdmin(session.email)) {
      setSignedInEmail(session.email);
      setIsAuthenticated(true);
    }
  }, [idleTimeoutEnabled]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const bump = () => touchAdminActivity();
    window.addEventListener('click', bump);
    window.addEventListener('keydown', bump);
    return () => {
      window.removeEventListener('click', bump);
      window.removeEventListener('keydown', bump);
    };
  }, [isAuthenticated]);

  // The signed-in account moved out from under this screen — a till sign-in in
  // another tab, or a sign-out somewhere else. Drop back to the sign-in card
  // and say why; every read below would otherwise be refused.
  useEffect(() => {
    if (!isAuthenticated || !signedInEmail) return;
    const stillAdmin = account && isPosAdmin(account)
      && account.trim().toLowerCase() === signedInEmail.trim().toLowerCase();
    if (stillAdmin) return;

    setIsAuthenticated(false);
    setSignedInEmail('');
    setLoginError(account
      ? `This browser is now signed in as ${account}. Please sign in again to administer.`
      : 'Your administrator session ended because this browser signed out. Please sign in again.');
  }, [account, isAuthenticated, signedInEmail]);

  /**
   * Signs an administrator in. Called by PosLoginForm once both fields pass
   * its inline checks; a refusal is reported through loginError.
   *
   * @param {string} email
   * @param {string} password
   * @param {boolean} remember - "Keep me signed in": keep the session after the tab closes
   * @returns {Promise<void>}
   */
  const handleLogin = async (email, password, remember) => {
    setLoginError('');
    try {
      const credential = await signInWithEmailAndPassword(auth, email, password);
      const authenticatedEmail = credential.user?.email || email;

      if (!isPosAdmin(authenticatedEmail)) {
        await signOut(auth).catch(() => {});
        await initializeDefaultUser().catch(() => {});
        setLoginError('That account is not an administrator.');
        return;
      }

      setSignedInEmail(authenticatedEmail);
      setIsAuthenticated(true);
      startAdminSession(authenticatedEmail, remember);
      logPosAdminEvent({
        action: POS_ADMIN_AUDIT_ACTIONS.ADMIN_SIGNED_IN,
        summary: { keptSignedIn: remember ? 'Yes' : 'No — ends when the tab closes' },
        actor: authenticatedEmail,
      });
    } catch (error) {
      console.error('POS admin sign-in failed:', error.code);
      // A wrong password and an unknown account read alike on purpose, so the
      // form cannot be used to discover which accounts exist.
      if (['auth/invalid-credential', 'auth/user-not-found', 'auth/wrong-password'].includes(error.code)) {
        setLoginError('Invalid email or password.');
      } else if (error.code === 'auth/invalid-email') {
        setLoginError('Please enter a valid email format.');
      } else {
        setLoginError('Login failed. Please try again.');
      }
    }
  };

  const handleLogout = async () => {
    // The admin trail is only writable as an administrator, so the entry has
    // to leave BEFORE the sign-out below — but never holds it up for long.
    // Awaited before the sign-in card comes back, so nobody can sign in again
    // in the gap and then be signed straight out by this.
    await Promise.race([
      logPosAdminEvent({ action: POS_ADMIN_AUDIT_ACTIONS.ADMIN_SIGNED_OUT, actor: signedInEmail }),
      new Promise((resolve) => { setTimeout(resolve, LOGIN_SIGN_OUT_AUDIT_WAIT_MS); }),
    ]);
    clearAdminActivity();
    setIsAuthenticated(false);
    setSignedInEmail('');
    try {
      await signOut(auth);
      // Back to the shared account, so the screens that need auth != null
      // keep working after an administrator leaves.
      await initializeDefaultUser();
    } catch (error) {
      console.error('POS admin sign-out failed:', error);
    }
  };

  /** Opens the registration screen with a client loaded. */
  const handleEdit = (client) => {
    setEditing(client);
    setTab('registration');
  };

  /** Opens the registration screen with a blank form. */
  const handleAdd = () => {
    setEditing(null);
    setTab('registration');
  };

  /** After a save, go back to the cards — the list is the proof it worked. */
  const handleSaved = () => {
    setEditing(null);
    setTab('clients');
  };

  if (configLoading) {
    return loadingSpinner('', BRAND_LOGO_URL);
  }

  if (!isAuthenticated) {
    return (
      <PosLoginScreen
        title={TAB_DISPLAY_NAME}
        subtitle={LOGIN_ADMIN_SUBTITLE}
      >
        <PosLoginForm
          onSubmit={handleLogin}
          error={loginError}
          forgotMessage={LOGIN_FORGOT_ADMIN}
        />
      </PosLoginScreen>
    );
  }

  const renderTab = () => {
    switch (tab) {
      case 'registration':
        return (
          <PosClientForm
            client={editing}
            onSaved={handleSaved}
            onCancel={handleSaved}
            signedInEmail={signedInEmail}
          />
        );
      case 'plans':
        return <PosSubscriptionPlans signedInEmail={signedInEmail} />;
      case 'signups':
        return <PosSignupRequests signedInEmail={signedInEmail} />;
      case 'config':
        return <PosAdminConfigScreen signedInEmail={signedInEmail} />;
      case 'clients':
      default:
        return <PosClientList onEdit={handleEdit} onAdd={handleAdd} signedInEmail={signedInEmail} />;
    }
  };

  return (
    <div className="pos-shell">
      <PosTopBar
        title={`${TAB_DISPLAY_NAME} - Admin`}
        logoUrl={BRAND_LOGO_URL}
        onOpenMenu={() => setMenuOpen(true)}
      />

      <PosSidebar
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        brandName={TAB_DISPLAY_NAME}
        logoUrl={BRAND_LOGO_URL}
        navLabel="Admin screens"
        tabs={POS_ADMIN_TABS}
        activeTab={tab}
        onTabChange={(key) => { setEditing(null); setTab(key); }}
        signedInEmail={signedInEmail}
        onSignOut={handleLogout}
      />
      <main className="pa-main">{renderTab()}</main>
    </div>
  );
}

export default PosAdminPage;
